/**
 * src/components/games/launchers/DrawTogetherSoloLauncher.tsx
 *
 * Solo (single-player) launcher for DrawTogether. Wraps the existing
 * DrawTogetherActivity with `roomId={null}` so the internal
 * NetworkManager setup early-returns — no voice channel, no host, no
 * peers. The brush / layer / export / paste-image engine is fully local
 * state and continues to work without any network code paths firing.
 * (Must be `null`, not `undefined` — DrawTogetherActivity defaults an
 * `undefined` roomId to the shared 'vc' room for the multiplayer path.)
 *
 * This used to self-register a SECOND manifest ('draw-together-solo'),
 * which made DrawTogether appear twice in the arcade picker. It no longer
 * registers anything: the single 'draw-together' manifest declares
 * `multiplayer: true` AND `solo: true`, and GameManager dispatches here via
 * SOLO_COMPONENTS when the user picks Solo on the DrawTogether tile.
 * Multiplayer still routes to DrawTogetherActivity via STATIC_COMPONENTS.
 */
import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import DrawTogetherActivity from '../DrawTogetherActivity';
import type { GameRuntimeProps } from '../../../game/framework/types';

/**
 * Solo Activity. Mirrors the onChange-on-mount lifecycle ping used by
 * IframeGameActivity so the host's activity pill lights up reliably
 * when entered via the rail launch / GameManager dispatch.
 */
const DrawTogetherSoloActivity: React.FC<GameRuntimeProps> = ({
  onExit,
  onChange,
}) => {
  useEffect(() => {
    onChange?.('draw-together');
    return () => onChange?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative w-full h-full bg-black text-white flex flex-col overflow-hidden">
      {/*
        Pass roomId={null} — NOT undefined — so DrawTogetherActivity's
        network setup useEffect early-returns. DrawTogetherActivity
        defaults roomId to the shared 'vc' room via a default parameter,
        which only triggers on `undefined`; an explicit `null` bypasses
        that default and reaches the `if (!roomId) return;` guard, which
        is what actually skips the NetworkManager connection. Passing
        `undefined` here would silently rejoin the shared 'vc' room and
        sync strokes with any other client in it.
      */}
      <DrawTogetherActivity
        roomId={null}
        currentUserId="solo"
        currentUsername="Solo Artist"
      />

      {/*
        DrawTogetherActivity only renders its own exit button when it's
        given an `onExit` prop, and we deliberately don't pass one above
        (solo mode has its own onExit here, from GameManager, that isn't
        the same closure). So we overlay a dedicated exit control instead.
      */}
      <button
        onClick={onExit}
        aria-label="Exit practice canvas"
        className="absolute top-3 right-3 z-50 px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-bold uppercase tracking-widest hover:bg-red-500/20 transition-colors flex items-center gap-2 shadow-lg backdrop-blur-md"
      >
        <X size={14} />
        Exit
      </button>
    </div>
  );
};

export default DrawTogetherSoloActivity;
